/**
 * Theme coverage panel — "are we actually getting arts, crafts, markets, plants,
 * volunteering and puzzles?"
 *
 * Reads `GET /api/mgmt/coverage/themes`. The whole point of the arts/nature/
 * markets expansion was that it be MEASURABLE: before the tag vocabulary was
 * widened, a watercolour workshop and a sourdough workshop both tagged as
 * `workshop`, so nobody could tell whether coverage was improving without
 * reading every title by hand.
 *
 * Non-intrusive by design: it lives on the admin Coverage page and loads only
 * when that page is opened.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

interface ThemeCoverage {
  theme: string;
  events: number;
  share: number;
  topVenues: string[];
}

interface ThemeCoverageReport {
  from: string;
  to: string;
  months: number;
  totalAcceptedEvents: number;
  themes: ThemeCoverage[];
  truncatedPartitions: boolean;
}

const THEME_LABELS: Record<string, string> = {
  'arts-visual': 'Visual arts & exhibitions',
  'arts-crafts': 'Arts & crafts',
  'markets-vintage': 'Markets, vintage & antiques',
  'nature-plants': 'Nature & plants',
  volunteer: 'Volunteering',
  'performing-arts': 'Performing arts',
  other: 'Everything else'
};

/** Themes we are actively trying to grow — shown first and flagged. */
const TARGET_THEMES = new Set([
  'arts-visual', 'arts-crafts', 'markets-vintage', 'nature-plants', 'volunteer', 'performing-arts'
]);

export default function ThemeCoveragePanel() {
  const [months, setMonths] = useState(3);
  const [report, setReport] = useState<ThemeCoverageReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (m: number) => {
    setLoading(true);
    setError(null);
    try {
      setReport(await api.get<ThemeCoverageReport>(`/api/mgmt/coverage/themes?months=${m}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load theme coverage.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(months); }, [load, months]);

  const ordered = report
    ? [...report.themes].sort((a, b) => {
        const at = TARGET_THEMES.has(a.theme) ? 0 : 1;
        const bt = TARGET_THEMES.has(b.theme) ? 0 : 1;
        if (at !== bt) return at - bt;
        return b.events - a.events;
      })
    : [];

  return (
    <section className="mb-5">
      <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-2">
        <h2 className="h4 mb-0">Content coverage</h2>
        <div className="d-flex align-items-center gap-2">
          <label htmlFor="coverage-months" className="form-label mb-0 small">Window</label>
          <select
            id="coverage-months"
            className="form-select form-select-sm mh-input-narrow"
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
          >
            <option value={1}>1 month</option>
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
          </select>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => void load(months)} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <p className="form-text mt-0">
        Accepted events grouped by theme, from event tags with a title-keyword fallback for
        events ingested before the tag vocabulary was widened. The themes in bold are the ones
        we are actively trying to grow.
      </p>

      {error && <div className="alert alert-warning" role="alert">{error}</div>}

      {report && !error && (
        <>
          <div className="small text-body-secondary mb-2">
            {report.totalAcceptedEvents.toLocaleString()} accepted events between{' '}
            {report.from.slice(0, 10)} and {report.to.slice(0, 10)}
            {report.truncatedPartitions && ' (window too wide to scope by partition — counts may be partial)'}
          </div>
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th scope="col">Theme</th>
                <th scope="col" className="text-end">Events</th>
                <th scope="col" className="text-end">Share</th>
                <th scope="col">Top matches</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((t) => (
                <tr key={t.theme}>
                  <th scope="row" className={TARGET_THEMES.has(t.theme) ? 'fw-semibold' : 'fw-normal text-body-secondary'}>
                    {THEME_LABELS[t.theme] ?? t.theme}
                  </th>
                  <td className="text-end">{t.events.toLocaleString()}</td>
                  <td className="text-end">{Math.round(t.share * 100)}%</td>
                  <td className="small text-body-secondary">
                    {t.topVenues.length ? t.topVenues.join(' · ') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
